import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import type { TableProps } from 'antd'
import { getCancellationToken } from './core'

import { useApi } from './api'
import { getCancellable, queryKeys, queryify } from './queries-common'

type OnChange = NonNullable<TableProps['onChange']>
type Filters = Parameters<OnChange>[1] | null

interface DeleteParams {
  companyId: string
}

export const useSuppliersAndManufacturers = (
  filters: Filters = null,
  sorters: { columnKey?: string; order?: string } = {}
) => {
  const api = useApi()
  let index = 0
  const pageSize = 300

  return useInfiniteQuery(
    [queryKeys.getSuppliersAndManufacturers, !!api],
    () => {
      if (!api) {
        return getCancellable()
      }

      const cancellationToken = getCancellationToken()

      type Params = {
        sortBy: string
        sortOrder: 'Asc' | 'Desc'
        isManufacturer: boolean | null
        companyName: string | null
        companyCertificates: string[] | null
        cancelToken: any
        pageSize?: number
        index?: number
      }
      const params: Params = {
        sortBy: 'name',
        sortOrder: 'Asc',
        isManufacturer: null,
        companyName: null,
        companyCertificates: null,
        cancelToken: cancellationToken.token
      }
      if (sorters && sorters.columnKey) {
        params.sortBy = sorters.columnKey as string
        params.sortOrder = sorters.order === 'ascend' ? 'Asc' : 'Desc'
      }
      if (filters) {
        if (filters.name) {
          params.companyName = (filters.name[0] as string).trim()
        }
        if (filters.isManufacturer) {
          params.isManufacturer =
            filters.isManufacturer[0] === 'manufacturer' ? true : false
        }
        if (filters.companyCertificates) {
          params.companyCertificates = filters.companyCertificates as string[]
        }
      }
      params.pageSize = pageSize
      params.index = index

      return queryify(
        api.getSuppliersAndManufacturers(params),
        cancellationToken.cancel
      )
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length < pageSize) {
          return false
        } else {
          index = pages.length * pageSize
          return true
        }
      },
      getPreviousPageParam: () => false,
      keepPreviousData: true
    }
  )
}

export const useManufacturer = (companyId: string) => {
  const api = useApi()

  return useQuery([queryKeys.getManufacturer(companyId), !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getManufacturer(companyId))
  })
}

export const useManufacturerDelete = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteParams>(
    async ({ companyId }) => {
      if (!api) throw new Error('API not initialized')
      return queryify(api.deleteManufacturer(companyId))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getSuppliersAndManufacturers]
        })
      },
      retry: 3,
      retryDelay: attempt => 1000 * attempt
    }
  )
}

export const useSupplier = (companyId: string) => {
  const api = useApi()

  return useQuery([queryKeys.getSupplier(companyId), !!api], () => {
    if (!api) {
      return getCancellable()
    }

    return queryify(api.getSupplier(companyId))
  })
}

export const useSupplierDelete = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteParams>(
    async ({ companyId }) => {
      if (!api) throw new Error('API not initialized')
      return queryify(api.deleteSupplier(companyId))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getSuppliersAndManufacturers]
        })
      },
      retry: 3,
      retryDelay: attempt => 1000 * attempt
    }
  )
}

export const useSupplierManufacturerUpsert = (
  isNew = false,
  type: 'supplier' | 'manufacturer' | undefined = 'supplier'
) => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation(
    (company: any) => {
      if (!api) throw new Error('API not initialized')
      if (isNew) {
        if (type === 'supplier') {
          return queryify(api.registerSupplier(company))
        } else {
          return queryify(api.registerManufacturer(company))
        }
      } else {
        if (type === 'supplier') {
          return queryify(api.updateSupplier(company))
        } else {
          return queryify(api.updateManufacturer(company))
        }
      }
    },
    {
      onSuccess: response => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.getSuppliersAndManufacturers]
        })
        if (type === 'supplier') {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.getSupplier(response.id)]
          })
        } else {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.getManufacturer(response.id)]
          })
        }
      }
    }
  )
}
